import { useEffect, useRef } from 'react';
import { useAppStore } from '../store/useAppStore';
import { useOrgRealtime } from '../hooks/useOrgRealtime';
import { useSystemHealth } from '../hooks/useSystemHealth';
import { useSSE } from '../hooks/useSSE';

export function SSEProvider({ children }: { children: React.ReactNode }) {
  useSSE();         // backend-push computed events: agent_activity, signal_fired, market_movement, etc.
  useOrgRealtime(); // Supabase Realtime: DB-level alerts, recommendations, bankroll, watchlist, bets
  useSystemHealth(); // polls /api/health every 60s → sets systemHealth in Zustand

  const {
    fetchGames,
    fetchAlerts,
    fetchWatchlist,
    setLastRefreshAt,
    selectedDate,
    games,
  } = useAppStore();

  // Keep stable refs so the setInterval callback always reads current values
  // without needing to be re-created (which would reset the timer).
  const gamesRef = useRef(games);
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { gamesRef.current = games; }, [games]);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  useEffect(() => {
    // Initial data load (non-user-specific or cheap to double-call)
    fetchGames();
    fetchAlerts();
    fetchWatchlist();

    // Poll for live game scores every 30s when games are in progress.
    // Supabase Realtime handles DB-change events; this covers the MLB Stats API
    // live score feed which is not stored in the DB.
    const interval = setInterval(() => {
      setLastRefreshAt(new Date().toISOString());
      const hasLiveGames = gamesRef.current.some(
        (ctx) => ctx.game.status.abstractGameState === 'Live',
      );
      if (hasLiveGames) fetchGames(selectedDateRef.current);
    }, 30_000);

    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally mounts once; refs stay current

  return <>{children}</>;
}
